import type { Perm, Role } from '@sj/shared';
import type { IconName } from '../components/Icon.js';

// Menu per peran, seksi, bottom-nav, judul & deskripsi — sama dengan NAV/SECTION/BOTTOM/DESC/titles v1 (admin.js).

export type NavItem = [view: string, label: string, icon: IconName];

export const NAV: Record<Role, NavItem[]> = {
  owner: [
    ['beranda', 'Beranda', 'home'],
    ['operasi', 'Operasi', 'list'],
    ['spo', 'Verifikasi SPO', 'doc'],
    ['nota', 'Nota & Kirim', 'truck'],
    ['rute', 'Rute Kirim', 'nav'],
    ['peta', 'Peta Driver', 'map'],
    ['keuangan', 'Keuangan', 'wallet'],
    ['kunjungan', 'Kunjungan (DKS)', 'pin'],
    ['toko', 'Toko', 'store'],
    ['laporan', 'Rekap & Margin', 'chart'],
    ['stok', 'Stok', 'box'],
    ['biaya', 'Biaya', 'receipt'],
    ['produk', 'Produk & Harga', 'tag'],
    ['master', 'Data Master', 'list'],
    ['pengguna', 'Pengguna', 'users'],
    ['peran', 'Peran & Akses', 'shield'],
    ['pengaturan', 'Pengaturan', 'settings'],
    ['panduan', 'Panduan', 'headset'],
  ],
  admin: [
    ['beranda', 'Beranda', 'home'],
    ['operasi', 'Operasi', 'list'],
    ['spo', 'Verifikasi SPO', 'doc'],
    ['nota', 'Nota & Kirim', 'truck'],
    ['rute', 'Rute Kirim', 'nav'],
    ['peta', 'Peta Driver', 'map'],
    ['keuangan', 'Keuangan', 'wallet'],
    ['kunjungan', 'Kunjungan (DKS)', 'pin'],
    ['toko', 'Toko', 'store'],
    ['laporan', 'Rekap & Margin', 'chart'],
    ['stok', 'Stok', 'box'],
    ['biaya', 'Biaya', 'receipt'],
    ['produk', 'Produk & Harga', 'tag'],
    ['master', 'Data Master', 'list'],
    ['panduan', 'Panduan', 'headset'],
  ],
  sales: [
    ['beranda', 'Beranda', 'home'],
    ['spo-baru', 'SPO Baru', 'docplus'],
    ['spo', 'SPO Saya', 'doc'],
    ['dks', 'Kunjungan (DKS)', 'pin'],
    ['toko', 'Toko Saya', 'store'],
    ['laporan', 'Rekap Saya', 'chart'],
    ['panduan', 'Panduan', 'headset'],
  ],
  driver: [
    ['tugas', 'Hari Ini', 'home'],
    ['muat', 'Muat', 'box'],
    ['antar', 'Antar', 'truck'],
    ['setoran', 'Setoran', 'wallet'],
    ['panduan', 'Panduan', 'headset'],
  ],
  toko: [
    ['toko-home', 'Beranda', 'home'],
    ['pesan', 'Pesan', 'bag'],
    ['pesanan', 'Pesanan Saya', 'receipt'],
    ['lacak', 'Lacak Kiriman', 'map'],
    ['panduan', 'Panduan', 'headset'],
  ],
};

export const SECTION: Record<string, string> = {
  operasi: 'Operasi',
  spo: 'Operasi',
  'spo-baru': 'Operasi',
  nota: 'Operasi',
  rute: 'Operasi',
  peta: 'Operasi',
  keuangan: 'Keuangan',
  kunjungan: 'Lapangan',
  dks: 'Lapangan',
  toko: 'Lapangan',
  laporan: 'Laporan',
  stok: 'Laporan',
  biaya: 'Laporan',
  produk: 'Data',
  master: 'Data',
  pengguna: 'Sistem',
  peran: 'Sistem',
  pengaturan: 'Sistem',
  panduan: 'Bantuan',
};
export const SEC_ORDER = ['Utama', 'Operasi', 'Keuangan', 'Lapangan', 'Laporan', 'Data', 'Sistem', 'Bantuan'];

/** Bottom-nav HP per peran ('more' = Lainnya/menu, 'akun' = Profil). */
export const BOTTOM: Record<Role, string[]> = {
  owner: ['beranda', 'operasi', 'nota', 'peta', 'more'],
  admin: ['beranda', 'operasi', 'nota', 'keuangan', 'more'],
  sales: ['beranda', 'spo-baru', 'dks', 'spo', 'more'],
  driver: ['tugas', 'muat', 'antar', 'setoran', 'akun'],
  toko: ['toko-home', 'lacak', 'pesan', 'pesanan', 'akun'],
};

/**
 * Hak yang dibutuhkan untuk membuka tiap menu (salah satu). Menu tanpa entri: semua peran jenis itu (Panduan) atau
 * hanya owner (Pengguna, Peran & Akses, Pengaturan — hanya ada di NAV.owner). Peran kustom memakai NAV jenis dasarnya.
 */
export const VIEW_PERM: Partial<Record<string, Perm[]>> = {
  beranda: ['beranda.lihat'],
  operasi: ['operasi.lihat'],
  spo: ['spo.lihat'],
  'spo-baru': ['spo.buat'],
  nota: ['nota.lihat'],
  rute: ['rute.lihat'],
  peta: ['peta.lihat'],
  keuangan: ['keuangan.lihat'],
  kunjungan: ['kunjungan.lihat'],
  dks: ['kunjungan.lihat'],
  toko: ['toko.lihat'],
  laporan: ['laporan.lihat'],
  stok: ['stok.lihat'],
  biaya: ['biaya.lihat'],
  produk: ['produk.lihat'],
  master: ['master.kelola'],
  tugas: ['tugas.antar'],
  muat: ['tugas.antar'],
  antar: ['tugas.antar'],
  setoran: ['tugas.antar'],
};

/** Menu yang boleh dibuka pengguna ini (menu jenis dasar, disaring hak). */
export const navFor = (role: Role, can: (p: Perm) => boolean): NavItem[] =>
  NAV[role].filter(([v]) => !VIEW_PERM[v] || (VIEW_PERM[v] ?? []).some(can));

/** Halaman awal: halaman awal jenis dasar bila boleh, selain itu menu pertama yang boleh. */
export const homeFor = (role: Role, list: NavItem[]): string => {
  const h = homeView(role);
  return list.some((n) => n[0] === h) ? h : (list[0]?.[0] ?? 'akun');
};

/** Halaman yang boleh dibuka tapi tidak tampil di menu. */
export const HIDDEN_VIEWS = ['akun', 'menu'];

export const homeView = (role: Role): string => ({ driver: 'tugas', toko: 'toko-home' })[role as 'driver' | 'toko'] || 'beranda';

export const DESC: Record<string, string> = {
  beranda: 'Ringkasan hari ini',
  operasi: 'Alur harian: siapkan → jalan → kembali',
  'spo-baru': 'Pesanan baru dari toko',
  spo: 'Surat pesanan & statusnya',
  nota: 'Nota, muat & pengiriman',
  rute: 'Satu kali jalan & rekap loading',
  peta: 'Posisi driver secara live',
  keuangan: 'Setoran, transfer & piutang',
  stok: 'Masuk, opname & kartu stok',
  laporan: 'Omzet, margin & pertumbuhan',
  kunjungan: 'Rekap kunjungan sales (CB/EC/NOO)',
  dks: 'Check-in kunjungan toko',
  toko: 'Daftar toko / client',
  biaya: 'Biaya operasional',
  produk: 'Produk & harga jual',
  master: 'Daftar pilihan: satuan, divisi, area, rekening, alasan',
  pengguna: 'Akun sales, driver, admin & toko',
  peran: 'Peran & hak akses tiap menu',
  pengaturan: 'Usaha, aturan, dokumen cetak & log',
  tugas: 'Langkah & ringkasan pengiriman hari ini',
  muat: 'Checklist muat & mulai perjalanan',
  antar: 'Navigasi, tujuan & serah terima',
  setoran: 'Cash di tangan & yang selesai',
  'toko-home': 'Ringkasan toko',
  pesan: 'Pesan langsung ke distributor',
  pesanan: 'Status pesanan & tagihan',
  lacak: 'Posisi driver menuju toko',
  panduan: 'Cara pakai aplikasi, istilah & tanya jawab',
};

export const TITLES: Record<string, string> = {
  akun: 'Profil',
  beranda: 'Beranda',
  biaya: 'Biaya Operasional',
  dks: 'Kunjungan (DKS)',
  keuangan: 'Keuangan',
  kunjungan: 'Kunjungan (DKS)',
  lacak: 'Lacak Kiriman',
  laporan: 'Rekap & Margin',
  master: 'Data Master',
  menu: 'Menu',
  nota: 'Nota & Pengiriman',
  operasi: 'Operasi Harian',
  panduan: 'Panduan',
  pengaturan: 'Pengaturan',
  pengguna: 'Pengguna',
  peran: 'Peran & Akses',
  pesanan: 'Pesanan Saya',
  pesan: 'Pesan Barang',
  peta: 'Peta Live (Driver & Sales)',
  produk: 'Produk & Harga',
  rute: 'Rute Kirim',
  'spo-baru': 'SPO Baru',
  spo: 'Surat Pesanan (SPO)',
  stok: 'Stok Barang',
  'toko-home': 'Beranda',
  toko: 'Toko / Client',
  tugas: 'Pengiriman Hari Ini',
  muat: 'Muat Barang',
  antar: 'Antar & Serah Terima',
  setoran: 'Setoran',
};

/** Kartu promo di sidebar per peran: [judul, teks, tujuan, tombol]. */
export const PROMO: Partial<Record<Role, [string, string, string, string]>> = {
  owner: ['Pantau driver', 'Lihat posisi pengiriman secara live.', '/peta', 'Buka peta'],
  admin: ['Antrian SPO', 'Verifikasi pesanan sales jadi nota.', '/spo', 'Verifikasi'],
  sales: ['Order cepat', 'Buat SPO langsung dari toko.', '/spo-baru', 'Buat SPO'],
  toko: ['Lacak kiriman', 'Lihat posisi driver menuju toko Anda.', '/lacak', 'Lacak'],
};

/** Label pendek bottom-nav (v1: {…}[v] || label kata pertama). */
export const bottomLabel = (view: string, label: string, role: Role): string =>
  ({
    'spo-baru': 'SPO Baru',
    spo: role === 'sales' ? 'SPO Saya' : 'Verifikasi',
    laporan: 'Rekap',
    pesanan: 'Pesanan',
    lacak: 'Lacak',
    tugas: 'Hari Ini',
  })[view] ||
  label.split(' ')[0] ||
  label;
